import type React from 'react';

import Grid from '@mui/material/Grid';
import TableCell from '@mui/material/TableCell';
import classNames from 'classnames';

import ArrowDownIcon from 'assets/icons/icn _ 16px _ sort down.svg?react';
import ArrowUpIcon from 'assets/icons/icn _ 16px _ sort up.svg?react';
import { useStyles } from 'components/tables/dndStandardTable/tableComponents/styles';
import {
  type CellConfigInterface,
  type ColumnData,
  type RowData,
  type SortableConfigInterface,
} from 'components/tables/dndStandardTable/types';

interface TableHeadCellProps<T extends RowData> {
  cellConfig?: CellConfigInterface;
  column: ColumnData<T>;
  index?: number;
  sortableConfig?: SortableConfigInterface;
  onCellClick?: (event?: React.MouseEvent<HTMLDivElement, MouseEvent>, rowIndex?: number) => void;
}

const TableHeadCell = <T extends RowData>({
  cellConfig,
  column,
  index,
  sortableConfig,
  onCellClick,
}: TableHeadCellProps<T>) => {
  const { classes, cx } = useStyles();
  const className = column.getTitle?.()?.className;
  const headerClassName = column.getTitle?.()?.headerClassName;

  const ascIcon = sortableConfig?.sortIcons?.asc || <ArrowUpIcon className={classes.sortIcon} />;
  const descIcon = sortableConfig?.sortIcons?.desc || <ArrowDownIcon className={classes.sortIcon} />;
  const noIcon = sortableConfig?.sortIcons?.none || null;
  const getIcon =
    sortableConfig?.orderBy === column.key && column.sortable
      ? sortableConfig?.order === 'asc'
        ? ascIcon
        : sortableConfig?.order === 'desc'
          ? descIcon
          : noIcon
      : noIcon;
  const handleOnSort = () => {
    if (column.sortable && sortableConfig.onSort) {
      sortableConfig.onSort(column.key);
    }
  };
  const handleClick = column.sortable
    ? handleOnSort
    : (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => onCellClick?.(event, index);
  return (
    <>
      <TableCell
        key={column.key}
        className={cx(cellConfig?.className && cellConfig.className, className, classes.sortable)}
        id={column.key}
        width={column.width}
        onClick={handleClick}
      >
        <Grid container alignItems="center" className={headerClassName}>
          <Grid item>{column.getTitle?.()?.node || ''}</Grid>
          {!!getIcon && (
            <Grid item className={classes.sortIconWrapper}>
              {getIcon}
            </Grid>
          )}
        </Grid>
      </TableCell>
      {!!column.colSpan && (
        <TableCell
          key={column.key}
          className={classNames(cellConfig.className, className)}
          colSpan={column.colSpan}
          id={column.key}
        />
      )}
    </>
  );
};

export default TableHeadCell;
